import type { ConceptDef } from '../types';

const pairs = (id: string, name: string, items: { say: string; answer: string }[], description: string): ConceptDef => ({
  kind: 'memoryPairs',
  id,
  label: name,
  category: 'Matching',
  description,
  config: { items, pairsPerBoard: 4, faceUp: false, chipSpeech: true },
});

export const memoryAnimals = pairs('memory-animals', 'Memory Pairs: Animals', [
  { say: '🐶 dog', answer: '🐶 dog' }, { say: '🐱 cat', answer: '🐱 cat' }, { say: '🐮 cow', answer: '🐮 cow' },
  { say: '🐷 pig', answer: '🐷 pig' }, { say: '🐸 frog', answer: '🐸 frog' }, { say: '🐵 monkey', answer: '🐵 monkey' },
], 'Turn the cards over two at a time to find the matching animals.');

export const memoryCase = pairs('memory-case', 'Memory Pairs: Big and Small Letters', [
  { say: 'A', answer: 'a' }, { say: 'B', answer: 'b' }, { say: 'D', answer: 'd' }, { say: 'G', answer: 'g' },
  { say: 'M', answer: 'm' }, { say: 'R', answer: 'r' }, { say: 'T', answer: 't' },
], 'Match each capital letter with its small letter.');

export const memoryNumbers = pairs('memory-numbers', 'Memory Pairs: Number and Amount', [
  { say: '1', answer: '⚫' }, { say: '2', answer: '⚫⚫' }, { say: '3', answer: '⚫⚫⚫' },
  { say: '4', answer: '⚫⚫⚫⚫' }, { say: '5', answer: '⚫⚫⚫⚫⚫' },
], 'Match each number with the right number of dots.');

export const memoryTogether = pairs('memory-together', 'Memory Pairs: Go Together', [
  { say: '🧦 sock', answer: '👟 shoe' }, { say: '🪥 toothbrush', answer: '🦷 teeth' }, { say: '🔑 key', answer: '🔒 lock' },
  { say: '✏️ pencil', answer: '📄 paper' }, { say: '🪣 bucket', answer: '🏖️ beach' }, { say: '🐝 bee', answer: '🍯 honey' },
], 'Find the two things that go together.');

export const memoryFeelings = pairs('memory-feelings', 'Memory Pairs: Feelings', [
  { say: '😀 happy', answer: '😀 happy' }, { say: '😢 sad', answer: '😢 sad' }, { say: '😠 angry', answer: '😠 angry' },
  { say: '😨 scared', answer: '😨 scared' }, { say: '😴 tired', answer: '😴 tired' },
], 'Find the matching feeling faces.');

const order = (id: string, name: string, category: ConceptDef['category'], sequences: { label: string; items: string[] }[], description: string): ConceptDef => ({
  kind: 'slotOrder',
  id,
  label: name,
  category,
  description,
  config: { sequences, promptMode: 'AUDIO', chipSpeech: true, errorless: 'off' },
});

export const orderSentences = order('order-sentences', 'Build It: Sentences', 'Literacy', [
  { label: 'I want juice', items: ['I', 'want', 'juice'] },
  { label: 'I see a dog', items: ['I', 'see', 'a', 'dog'] },
  { label: 'The cat is sleeping', items: ['The', 'cat', 'is', 'sleeping'] },
  { label: 'Can I have help', items: ['Can', 'I', 'have', 'help'] },
  { label: 'I like the park', items: ['I', 'like', 'the', 'park'] },
], 'Drag the words into the spaces to build the sentence.');

export const orderSpellCvc = order('order-spell-cvc', 'Build It: Spell Short Words', 'Literacy', [
  { label: '🐱 cat', items: ['c', 'a', 't'] },
  { label: '🐶 dog', items: ['d', 'o', 'g'] },
  { label: '☀️ sun', items: ['s', 'u', 'n'] },
  { label: '🛏️ bed', items: ['b', 'e', 'd'] },
  { label: '🐷 pig', items: ['p', 'i', 'g'] },
  { label: '🎩 hat', items: ['h', 'a', 't'] },
], 'See the picture and drag the letters in to spell the word.');

export const orderSpellColours = order('order-spell-colours', 'Build It: Spell Colours', 'Literacy', [
  { label: '🟥 red', items: ['r', 'e', 'd'] },
  { label: '🟦 blue', items: ['b', 'l', 'u', 'e'] },
  { label: '🟩 green', items: ['g', 'r', 'e', 'e', 'n'] },
  { label: '🩷 pink', items: ['p', 'i', 'n', 'k'] },
], 'Drag the letters in to spell the colour.');

export const orderStories = order('order-stories', 'Build It: Story Order', 'Sequencing', [
  { label: 'Growing a plant', items: ['🌰 seed', '💧 water', '🌱 shoot', '🌻 flower'] },
  { label: 'Making a sandwich', items: ['🍞 bread', '🧈 butter', '🧀 cheese', '🥪 sandwich'] },
  { label: 'Building a snowman', items: ['❄️ snow', '⚪ roll a ball', '⛄ stack it', '🥕 add a nose'] },
], 'Drag the pictures into first, next and last.');

export const orderNumbers = order('order-numbers', 'Build It: Number Order', 'Math', [
  { label: 'One to five', items: ['1', '2', '3', '4', '5'] },
  { label: 'Six to ten', items: ['6', '7', '8', '9', '10'] },
  { label: 'Count in twos', items: ['2', '4', '6', '8', '10'] },
], 'Drag the numbers into order.');

const copy = (id: string, name: string, sequences: { label: string; items: string[] }[], description: string, modelStays = 'stays'): ConceptDef => ({
  kind: 'blockCopy',
  id,
  label: name,
  category: 'Matching',
  description,
  config: { sequences, modelStays, errorless: 'off' },
});

export const copyTwo = copy('copy-two', 'Copy the Pattern: 2 by 2', [
  { label: 'Red and blue', items: ['red', 'blue', 'blue', 'red'] },
  { label: 'Top and bottom', items: ['yellow', 'yellow', 'green', 'green'] },
  { label: 'Side by side', items: ['red', 'yellow', 'red', 'yellow'] },
  { label: 'One different', items: ['blue', 'blue', 'blue', 'orange'] },
], 'Copy a pattern of four coloured blocks.');

export const copyThree = copy('copy-three', 'Copy the Pattern: 3 by 3', [
  { label: 'Stripes', items: ['yellow', 'yellow', 'yellow', 'blue', 'blue', 'blue', 'yellow', 'yellow', 'yellow'] },
  { label: 'Cross', items: ['white', 'red', 'white', 'red', 'red', 'red', 'white', 'red', 'white'] },
  { label: 'Letter L', items: ['green', 'white', 'white', 'green', 'white', 'white', 'green', 'green', 'green'] },
  { label: 'Corners', items: ['blue', 'white', 'blue', 'white', 'yellow', 'white', 'blue', 'white', 'blue'] },
], 'Copy a pattern of nine coloured blocks.');

export const copyMemory = copy('copy-memory', 'Copy the Pattern: From Memory', [
  { label: 'Red and blue', items: ['red', 'blue', 'blue', 'red'] },
  { label: 'Stripes', items: ['yellow', 'yellow', 'yellow', 'blue', 'blue', 'blue', 'yellow', 'yellow', 'yellow'] },
  { label: 'Smile', items: ['white', 'white', 'white', 'white', 'purple', 'white', 'white', 'purple', 'white', 'white', 'white', 'white', 'purple', 'purple', 'purple', 'purple'] },
], 'Look at the pattern, then copy it after it hides.', 'hides');
